import React from 'react';
import { User } from '../types';
import toast from 'react-hot-toast';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';

interface SecurityCheckProps {
  user: User;
  children: React.ReactNode;
}

export const SecurityCheck: React.FC<SecurityCheckProps> = ({ user, children }) => {
  const [timeLeft, setTimeLeft] = React.useState<string>('');

  // Calculate block status at top level
  let blockUntilDate: Date | null = null;
  let isTemporary = false;
  let isBanExpired = false;

  if (user.kyc?.blockUntil) {
    // Handle Firestore Timestamp or Date or string
    const dateVal = user.kyc.blockUntil as any;
    if (dateVal && typeof dateVal.toDate === 'function') {
      blockUntilDate = dateVal.toDate();
    } else if (dateVal) {
      blockUntilDate = new Date(dateVal);
    }

    if (blockUntilDate && !isNaN(blockUntilDate.getTime())) {
      // If blockPermanent is explicitly true, treat as permanent regardless of date
      if (user.kyc.blockPermanent !== true) {
        if (blockUntilDate > new Date()) {
          isTemporary = true;
        } else {
          isBanExpired = true;
        }
      }
    }
  }

  // Attempt to auto-unban if expired
  React.useEffect(() => {
    if (isBanExpired && user.isBanned) {
      const autoUnban = async () => {
        try {
          const userRef = doc(db, 'users', user.id);
          await updateDoc(userRef, {
            isBanned: false,
            bannedAt: null,
            'kyc.blockUntil': null
          });
          toast.success('Your temporary ban has expired. Access restored.');
        } catch (error) {
          console.error('Failed to auto-unban (permissions might block this):', error);
          // User can still access app due to isBanExpired logic below, even if DB update fails
        }
      };
      autoUnban();
    }
  }, [isBanExpired, user.isBanned, user.id]);

  // Effect for countdown
  React.useEffect(() => {
    if (!user.isBanned || !isTemporary || !blockUntilDate) return;

    const update = () => {
      const now = new Date();
      const diff = blockUntilDate!.getTime() - now.getTime();

      if (diff <= 0) {
        setTimeLeft('Access Restored!');
        // Ideally we could trigger a re-render here to hide the screen,
        // but state update `setTimeLeft` works.
        // However, isBanExpired won't update until re-render.
        // Since we are inside SecurityCheck, a state change will trigger re-render,
        // and isBanExpired will be recalculated.
        return;
      }

      const d = Math.floor(diff / (1000 * 60 * 60 * 24));
      const h = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const m = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const s = Math.floor((diff % (1000 * 60)) / 1000);

      setTimeLeft(`${d}d ${h}h ${m}m ${s}s`);
    };

    update();
    const timer = setInterval(update, 1000);
    return () => clearInterval(timer);
  }, [user.isBanned, isTemporary, blockUntilDate]);

  // Check if user is banned
  if (user.isBanned && !isBanExpired) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: isTemporary
          ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' // Amber for temporary
          : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)', // Red for permanent
        padding: '20px',
        textAlign: 'center' as const,
        color: 'white'
      }}>
        <div style={{
          backgroundColor: '#ffffff',
          borderRadius: '20px',
          padding: '32px',
          maxWidth: '400px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
          border: '1px solid #e9ecef'
        }}>
          <div style={{
            fontSize: '64px',
            marginBottom: '8px'
          }}>
            {isTemporary ? '⏳' : '🚫'}
          </div>

          {isTemporary && timeLeft && (
            <div style={{
              fontSize: '24px',
              fontWeight: 'bold',
              color: '#d97706',
              marginBottom: '16px',
              fontFamily: 'monospace',
              background: '#fffbeb',
              padding: '8px 16px',
              borderRadius: '8px',
              display: 'inline-block'
            }}>
              {timeLeft}
            </div>
          )}

          <h1 style={{
            fontSize: '24px',
            fontWeight: 'bold',
            marginBottom: '16px',
            color: '#1a1a1a'
          }}>
            {isTemporary ? 'Account Temporarily Blocked' : 'Account Banned'}
          </h1>

          <p style={{
            fontSize: '16px',
            color: '#666',
            marginBottom: '20px',
            lineHeight: '1.5'
          }}>
            {isTemporary
              ? `Your account access has been temporarily restricted.`
              : 'Your account has been permanently banned due to suspicious or fraudulent activity.'}
          </p>

          {user.bannedAt && (
            <div style={{
              backgroundColor: '#f8f9fa',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '20px',
              border: '1px solid #e9ecef'
            }}>
              <div style={{
                fontSize: '14px',
                color: '#666',
                marginBottom: '4px'
              }}>
                {isTemporary ? 'Blocked on:' : 'Banned on:'}
              </div>
              <div style={{
                fontSize: '16px',
                fontWeight: '600',
                color: '#1a1a1a'
              }}>
                {user.bannedAt instanceof Date ? user.bannedAt.toLocaleDateString('en-IN', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                }) : (user.bannedAt as any)?.toDate?.().toLocaleDateString('en-IN', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                }) || 'N/A'}
              </div>
            </div>
          )}

          {user.lastAction && (
            <div style={{
              backgroundColor: '#f8f9fa',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '20px',
              border: '1px solid #e9ecef'
            }}>
              <div style={{
                fontSize: '14px',
                color: '#666',
                marginBottom: '4px'
              }}>
                Reason:
              </div>
              <div style={{
                fontSize: '16px',
                fontWeight: '600',
                color: '#1a1a1a'
              }}>
                {user.lastAction}
              </div>
            </div>
          )}

          <div style={{
            fontSize: '14px',
            color: '#999',
            lineHeight: '1.4'
          }}>
            {isTemporary
              ? 'Access will be automatically restored after this date.'
              : 'If you believe this is an error, please contact our support team.'}
          </div>
        </div>
      </div>
    );
  }

  // Check if user is flagged for suspicious activity
  if (user.isFlagged) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
        padding: '20px',
        textAlign: 'center' as const,
        color: 'white'
      }}>
        <div style={{
          backgroundColor: '#ffffff',
          borderRadius: '20px',
          padding: '32px',
          maxWidth: '400px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
          border: '1px solid #e9ecef'
        }}>
          <div style={{
            fontSize: '64px',
            marginBottom: '16px'
          }}>
            🚨
          </div>

          <h1 style={{
            fontSize: '24px',
            fontWeight: 'bold',
            marginBottom: '16px',
            color: '#1a1a1a'
          }}>
            Account Under Review
          </h1>

          <p style={{
            fontSize: '16px',
            color: '#666',
            marginBottom: '20px',
            lineHeight: '1.5'
          }}>
            Your account has been flagged for suspicious activity and is currently under review.
          </p>

          {user.flaggedAt && (
            <div style={{
              backgroundColor: '#f8f9fa',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '20px',
              border: '1px solid #e9ecef'
            }}>
              <div style={{
                fontSize: '14px',
                color: '#666',
                marginBottom: '4px'
              }}>
                Flagged on:
              </div>
              <div style={{
                fontSize: '16px',
                fontWeight: '600',
                color: '#1a1a1a'
              }}>
                {user.flaggedAt.toLocaleDateString('en-IN', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </div>
            </div>
          )}

          {user.lastAction && (
            <div style={{
              backgroundColor: '#f8f9fa',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '20px',
              border: '1px solid #e9ecef'
            }}>
              <div style={{
                fontSize: '14px',
                color: '#666',
                marginBottom: '4px'
              }}>
                Reason:
              </div>
              <div style={{
                fontSize: '16px',
                fontWeight: '600',
                color: '#1a1a1a'
              }}>
                {user.lastAction}
              </div>
            </div>
          )}

          <div style={{
            fontSize: '14px',
            color: '#999',
            lineHeight: '1.4'
          }}>
            Your account access is temporarily restricted. Please contact support for assistance.
          </div>
        </div>
      </div>
    );
  }

  // If user is not banned or flagged, show the normal app
  return <>{children}</>;
};